import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Skip check if returning from OAuth
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => { setUser(r.data); setLoading(false); })
      .catch(() => { setUser(null); setLoading(false); });
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  return { user, loading, login, logout, setUser };
};

export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = hash.split("session_id=")[1];
    if (!sessionId) { navigate("/"); return; }

    axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then(r => {
        navigate("/founder", { state: { user: r.data } });
      })
      .catch(() => {
        navigate("/");
      });
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#2d8cf0] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-sm">Inloggen...</p>
      </div>
    </div>
  );
};

export const LoginGate = ({ children, requireFounder = false }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();

  // If user was passed from AuthCallback
  const passedUser = location.state?.user;
  const activeUser = passedUser || user;

  if (loading && !passedUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#2d8cf0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activeUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="login-gate">
        <div className="max-w-sm w-full mx-4 text-center bg-white border border-[#E5E5E5] p-8 shadow-lg">
          <img src="/logo-small.png" alt="" className="h-12 w-12 mx-auto mb-4" />
          <h2 className="text-lg font-black mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>Toegang Beperkt</h2>
          <p className="text-sm text-[#52525B] mb-6">Log in met je Google account om toegang te krijgen.</p>
          <button onClick={login}
            className="w-full px-4 py-3 bg-[#2d8cf0] text-white font-bold text-sm hover:bg-[#1a6fd4] transition-colors flex items-center justify-center gap-2"
            data-testid="google-login-btn"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Inloggen met Google
          </button>
          {requireFounder && <p className="text-[10px] text-gray-400 mt-3">Alleen de founder heeft toegang tot dit dashboard.</p>}
        </div>
      </div>
    );
  }

  if (requireFounder && !activeUser.is_founder) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="access-denied">
        <div className="max-w-sm w-full mx-4 text-center bg-white border border-red-200 p-8">
          <h2 className="text-lg font-black text-red-700 mb-2">Geen Toegang</h2>
          <p className="text-sm text-[#52525B]">Dit dashboard is alleen toegankelijk voor de founder.</p>
        </div>
      </div>
    );
  }

  return children;
};
